import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@f2e/ui/components/base"
import { useTranslation } from "react-i18next"
import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateImageGenerationFieldDefinition,
  AgentTemplateToolValue,
} from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

export const ImageGenerationItemRenderer = ({
  item,
  locale,
  value,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  if (item.type !== "tool_image_generation") return null
  const definition = item as AgentTemplateImageGenerationFieldDefinition
  const current = (value as AgentTemplateToolValue | undefined) ?? {
    enabled: Boolean(definition.required || definition.enabledByDefault),
    configuration: {},
  }
  const configuration = current.configuration
  const update = (patch: Record<string, unknown>) =>
    onChange({
      enabled: current.enabled,
      configuration: { ...configuration, ...patch },
    })
  const trigger = String(configuration.trigger || definition.defaultTrigger)
  const stylePreset = String(configuration.stylePreset || "none")
  return (
    <ConfigurableFieldShell
      id={`template-${definition.key}`}
      title={resolveLocalizedText(definition.label, locale)}
      description={
        definition.description
          ? resolveLocalizedText(definition.description, locale)
          : undefined
      }
      enabled={current.enabled}
      required={definition.required}
      disabled={disabled}
      error={error}
      onEnabledChange={(enabled) => onChange({ enabled, configuration })}
    >
      <div className="space-y-4 border-t border-border pt-4">
        <div className="space-y-2">
          <Label>
            {t("workspace.agent.templates.imageGeneration.trigger")}
          </Label>
          <Select
            value={trigger}
            disabled={disabled}
            onValueChange={(next) => update({ trigger: next })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {definition.triggerOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {t(
                    `workspace.agent.templates.imageGeneration.triggers.${option}`
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {trigger === "custom" && (
          <div className="space-y-2">
            <Label>
              {t("workspace.agent.templates.imageGeneration.customRule")}
            </Label>
            <Textarea
              value={String(configuration.customRule || "")}
              maxLength={definition.customRuleMaxLength}
              disabled={disabled}
              onChange={(event) => update({ customRule: event.target.value })}
            />
          </div>
        )}
        {definition.stylePresets.length > 0 && (
          <div className="space-y-2">
            <Label>
              {t("workspace.agent.templates.imageGeneration.style")}
            </Label>
            <Select
              value={stylePreset}
              disabled={disabled}
              onValueChange={(next) =>
                update({ stylePreset: next === "none" ? null : next })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t("workspace.agent.templates.imageGeneration.noStyle")}
                </SelectItem>
                {definition.stylePresets.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {resolveLocalizedText(preset.label, locale)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {definition.allowCustomStyle && (
          <div className="space-y-2">
            <Label>
              {t("workspace.agent.templates.imageGeneration.customStyle")}
            </Label>
            <Textarea
              value={String(configuration.customStyle || "")}
              maxLength={definition.customStyleMaxLength}
              disabled={disabled}
              onChange={(event) => update({ customStyle: event.target.value })}
            />
          </div>
        )}
      </div>
    </ConfigurableFieldShell>
  )
}
